type WsEventHandler = (data: any) => void;
type WsConnectionHandler = (connected: boolean) => void;

const listeners = new Map<string, Set<WsEventHandler>>();
const connectionListeners = new Set<WsConnectionHandler>();
let ws: WebSocket | null = null;
let wsConnected = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let aliveTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_DELAY = 60000;
const WS_ALIVE_TIMEOUT = 90000; // Server pings every 30s, allow 3 cycles of grace
let consecutiveFailures = 0;

function setConnected(c: boolean) {
    if (wsConnected === c) return;
    wsConnected = c;
    connectionListeners.forEach(listener => listener(c));
}

function getSessionId(): string | null {
    const cookie = document.cookie.split('; ').find(c => c.startsWith('SessionId='));
    return cookie ? cookie.split('=')[1] : null;
}

function getWsUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const sessionId = getSessionId();
    return `${protocol}//${window.location.host}/ws?sessionId=${sessionId}`;
}

// Watchdog: if no server traffic arrives within WS_ALIVE_TIMEOUT, the connection is stale —
// force-close so onclose schedules a reconnect. Catches half-open TCP after PC sleep/wake, NAT
// rebind, or any silent network drop that leaves the socket "open" to the browser but dead.
// Browser WebSocket doesn't expose protocol-level pings to JS, so we rely on the server's
// app-level {type:'ping'} every 30s (and any other message) as the heartbeat signal.
function resetAliveTimer() {
    if (aliveTimer) clearTimeout(aliveTimer);
    aliveTimer = setTimeout(() => {
        console.log(`WS: No server traffic for ${WS_ALIVE_TIMEOUT / 1000}s, connection stale — reconnecting`);
        if (ws) {
            try { ws.close(); } catch { /* onclose will fire */ }
        }
    }, WS_ALIVE_TIMEOUT);
}

function clearAliveTimer() {
    if (aliveTimer) {
        clearTimeout(aliveTimer);
        aliveTimer = null;
    }
}

export function connectWebSocket() {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
        return;

    const sessionId = getSessionId();
    if (!sessionId) return;

    intentionalClose = false;
    ws = new WebSocket(getWsUrl());

    ws.onopen = () => {
        console.log('WS: Connected');
        consecutiveFailures = 0;
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
        resetAliveTimer();
        setConnected(true);
    };

    ws.onmessage = (event) => {
        resetAliveTimer();
        try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'ping') {
                ws?.send(JSON.stringify({ type: 'pong' }));
                return;
            }
            const handlers = listeners.get(msg.type);
            if (handlers) {
                for (const handler of handlers) {
                    handler(msg);
                }
            }
        } catch {
            // Ignore malformed messages
        }
    };

    ws.onclose = (event) => {
        clearAliveTimer();
        ws = null;
        setConnected(false);
        if (intentionalClose) return;
        if (event.code === 4001) {
            console.log('WS: Session expired, not reconnecting');
            return;
        }
        scheduleReconnect();
    };

    ws.onerror = () => {
        // onclose will fire after this
    };
}

function scheduleReconnect() {
    if (reconnectTimer) return;
    consecutiveFailures++;
    const delay = Math.min(RECONNECT_DELAY * Math.pow(2, consecutiveFailures - 1), MAX_RECONNECT_DELAY);
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connectWebSocket();
    }, delay);
}

export function disconnectWebSocket() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    clearAliveTimer();
    if (ws) {
        intentionalClose = true;
        ws.close();
        ws = null;
    }
}

/** Call after login/session restore — connects WS and keeps it alive across tab visibility
 *  changes. Do NOT close on visibilitychange: long-running operations (image builds, device
 *  commands) emit events that we'd miss during a backgrounded-tab gap. Browsers throttle
 *  timers in background tabs but do not require WS connections to be closed.
 *
 *  Silent half-open recovery is handled by the alive watchdog (resetAliveTimer above):
 *  if no server traffic for 90s, force-close → reconnect. visibilitychange is kept as a
 *  faster path when the user alt-tabs back. */
export function initWebSocket() {
    connectWebSocket();

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;
            if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                consecutiveFailures = 0;  // reset backoff — fresh user activity
                if (reconnectTimer) {
                    clearTimeout(reconnectTimer);
                    reconnectTimer = null;
                }
                connectWebSocket();
            }
        });
    }
}

export function onWsEvent(type: string, handler: WsEventHandler): () => void {
    if (!listeners.has(type))
        listeners.set(type, new Set());
    listeners.get(type)!.add(handler);

    // Return unsubscribe function
    return () => {
        listeners.get(type)?.delete(handler);
    };
}

/** Subscribe to WS connection state changes. Handler is invoked immediately with the
 *  current state, then again on every transition. Returns unsubscribe. */
export function onWsConnectionChange(handler: WsConnectionHandler): () => void {
    connectionListeners.add(handler);
    handler(wsConnected);
    return () => { connectionListeners.delete(handler); };
}

export function isWsConnected(): boolean {
    return wsConnected;
}
